/**
 * One-shot text seed transfer between formatter tools.
 *
 * Use case: in the JSON tool the user clicks "在 YAML 工具中打开"; we
 * stash the current JSON in `sessionStorage` and navigate to
 * `/tools/format/yaml`. The YAML tool reads + clears it on mount and
 * pre-fills the input. Pure session-scoped, never disk-persisted, and
 * deliberately one-shot so refreshing the YAML page doesn't replay
 * stale input.
 *
 * R2 (project default config rule): this is `sessionStorage`, not
 * `localStorage`, so it's covered by the explicit "sessionStorage one-
 * shot dismiss flag" exception. Don't migrate this to server prefs.
 */

const KEY = 'adh.tools.seed.v1';

export interface ToolSeed {
  /** Target tool path, e.g. '/tools/format/yaml'. */
  target: string;
  /** Initial input value to drop into the receiving tool. */
  text: string;
  /**
   * Optional hint for the receiver to pick a sensible mode (e.g. the
   * YAML tool reads `mode: 'json2yaml'` and pre-selects the JSON →
   * YAML pillbox).
   */
  mode?: string;
}

export function setToolSeed(seed: ToolSeed): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(seed));
  } catch {
    /* quota / private mode — silently drop, the receiver will just
     * show its empty state. */
  }
}

/**
 * Read + clear the seed for `target`. Returns `null` when there's no
 * seed or the seed targets a different tool. Calling it consumes the
 * seed (one-shot), so the receiver doesn't replay it on refresh.
 */
export function consumeToolSeed(target: string): ToolSeed | null {
  if (typeof sessionStorage === 'undefined') return null;
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    const parsed = JSON.parse(raw) as ToolSeed;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.target !== target) return null;
    if (typeof parsed.text !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}
